import React from 'react';
import { BaseStep, FormField, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import { TextArea } from '../TextArea';
import { FileUpload, getFileDisplayName } from '../FileUpload';
import { RadioButton } from '../RadioButton';
import { SDGSelector } from '../SDGSelector';
import { Toggle } from '../Toggle';
import { AddButton } from '../AddButton';
import { RemoveButton } from '../RemoveButton';
import companyIcon from '../../../../assets/icons/company2.png';
import pitchIcon from '../../../../assets/icons/pitch.png';
import writeIcon from '../../../../assets/write.png';
import investorsIcon from '../../../../assets/icons/company.png';
import CountrySelect from '../../../CountrySelect';
import { useAuth } from '../../../../contexts/AuthContext';
import { useProfile } from '../../../../contexts/ProfileContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../config/firebase';
import { normalizeExternalUrl } from '../../../../utils/normalizeExternalUrl';
import { checkPdfIsWidescreen } from '../../../../utils/pitchDeckDimensions';
import {
  countryRequiresStateSubdivision,
  isUnitedKingdomCountry,
  isUnitedStatesCountry,
} from '../../../../utils/investorPricingView';
import { UK_COUNTIES } from '../../../../constants/ukLocations';
import { usStates } from '../../../../constants/countries';
import CitySelect, { NATIVE_SELECT_CHEVRON_STYLE } from '../CitySelect';

const CompanyStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationResult,
}) => {
  const { user } = useAuth();
  const { startupProfile } = useProfile();
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<string>('');
  const [uploadError, setUploadError] = React.useState<string>('');
  const [pitchDeckFileName, setPitchDeckFileName] = React.useState(() =>
    startupProfile?.pitchDeckUrl ? getFileDisplayName(startupProfile.pitchDeckUrl) : ''
  );

  // Helper function to convert string to array (for backward compatibility)
  const stringToArray = (value: string | string[] | undefined): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) {
      return value.split(', ').filter(s => s);
    }
    return [];
  };

  // Helper function to convert string/number to number array (for backward compatibility)
  const stringToNumberArray = (value: string | number[] | undefined): number[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) {
      return value
        .split(',')
        .map(n => parseInt(n.trim()))
        .filter(n => !isNaN(n));
    }
    return [];
  };

  // Helper function to convert string to boolean (for backward compatibility)
  const stringToBoolean = (value: string | boolean | undefined): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  };

  // Initialize arrays from profile (handling both old string format and new array format)
  const socialMediaArray = stringToArray(startupProfile?.socialMediaUrls);
  const otherLinksArray = stringToArray(startupProfile?.otherLinks);
  const investorsArray = stringToArray(startupProfile?.investorsName);
  const selectedSDGsArray = stringToNumberArray(startupProfile?.selectedSDGs);
  const isSDGFocusedValue = stringToBoolean(startupProfile?.sustainableDevelopmentGoal);
  const hasInvestorsValue = stringToBoolean(startupProfile?.hasExistingInvestors);

  const {
    formData,
    errors,
    hasValidated,
    handleInputChange,
    validateForm,
    clearFieldError,
    setFormData,
    setErrors,
  } = useFormData({
    // Company section
    companyName: startupProfile?.companyName || '',
    url: startupProfile?.url || '',
    country: startupProfile?.country || user?.country || '',
    state: startupProfile?.state || '',
    city: startupProfile?.city || '',
    // Pitch section
    slogan: startupProfile?.slogan || '',
    pitchSummary: startupProfile?.pitchSummary || '',
    pitchDeckUrl: startupProfile?.pitchDeckUrl || '',
    videoPitchUrl: startupProfile?.videoPitchUrl || '',
    // Note: These fields will be handled as arrays via state
    socialMediaUrls: '',
    otherLinks: '',
    selectedSDGs: '',
    sustainableDevelopmentGoal: '',
    hasExistingInvestors: '',
    investorsName: '',
  });

  // State for dynamic inputs (arrays to track multiple inputs)
  const [socialMediaInputs, setSocialMediaInputs] = React.useState<string[]>(
    socialMediaArray.length > 0 ? socialMediaArray : ['']
  );
  const [otherLinksInputs, setOtherLinksInputs] = React.useState<string[]>(
    otherLinksArray.length > 0 ? otherLinksArray : ['']
  );
  const [investorsInputs, setInvestorsInputs] = React.useState<string[]>(
    investorsArray.length > 0 ? investorsArray : ['']
  );

  // Separate boolean state for the radio button
  const [isSDGFocused, setIsSDGFocused] = React.useState<boolean | null>(
    isSDGFocusedValue !== undefined ? isSDGFocusedValue : null
  );

  // State for selected SDGs
  const [selectedSDGs, setSelectedSDGs] = React.useState<number[]>(selectedSDGsArray);

  // State for existing investors toggle
  const [hasInvestors, setHasInvestors] = React.useState<boolean>(hasInvestorsValue ?? false);

  // Handle SDG selection
  const handleSDGSelect = (sdg: number) => {
    setSelectedSDGs(prev => {
      if (prev.includes(sdg)) {
        return prev.filter(s => s !== sdg);
      }
      return [...prev, sdg];
    });
  };

  // Initialize country from user data only when profile has no country saved
  React.useEffect(() => {
    if (user?.country && !startupProfile?.country) {
      setFormData(prev => ({ ...prev, country: user.country }));
    }
  }, [user, startupProfile, setFormData]);

  // Handle file upload
  const handleFileSelect = async (file: File | null) => {
    if (!file || !user) return;

    clearFieldError('pitchDeckUrl');
    setPitchDeckFileName(file.name);
    setUploadingFile(true);
    setUploadProgress('Checking slide dimensions...');
    setUploadError('');

    try {
      // 1. Verify 16:9 widescreen slide dimensions
      const arrayBuffer = await file.arrayBuffer();
      const dimensionCheck = await checkPdfIsWidescreen(arrayBuffer);
      if (!dimensionCheck.isValid) {
        setUploadError(dimensionCheck.error || 'Please use widescreen slides (16:9)');
        setPitchDeckFileName(
          typeof formData.pitchDeckUrl === 'string' && formData.pitchDeckUrl
            ? getFileDisplayName(formData.pitchDeckUrl)
            : ''
        );
        return;
      }

      setUploadProgress('Uploading...');

      // Read file as base64
      const reader = new FileReader();
      const base64Data = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1]; // Remove data:application/pdf;base64, prefix
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call Cloud Function to upload
      const uploadPitchDeck = httpsCallable(functions, 'uploadPitchDeck');
      const result = await uploadPitchDeck({
        userId: user.id,
        file: {
          name: file.name,
          contentType: file.type,
          data: base64Data,
        },
      });

      const data = result.data as any;
      if (data.success) {
        setFormData(prev => ({ ...prev, pitchDeckUrl: data.url }));
        setPitchDeckFileName(data.fileName || file.name);
        clearFieldError('pitchDeckUrl');
        setUploadProgress('Upload complete!');
        setTimeout(() => setUploadProgress(''), 2000);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  // Update formData when SDGs are selected (store as JSON string)
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      _selectedSDGsArray: JSON.stringify(selectedSDGs),
    }));
  }, [selectedSDGs, setFormData]);

  // Update formData when investors toggle changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      _hasExistingInvestorsBoolean: JSON.stringify(hasInvestors),
    }));
  }, [hasInvestors, setFormData]);

  // Update formData with array values (store as JSON strings)
  React.useEffect(() => {
    const filtered = socialMediaInputs.filter(s => s.trim() !== '');
    setFormData(prev => ({
      ...prev,
      _socialMediaUrlsArray: JSON.stringify(filtered),
    }));
  }, [socialMediaInputs, setFormData]);

  React.useEffect(() => {
    const filtered = otherLinksInputs.filter(s => s.trim() !== '');
    setFormData(prev => ({
      ...prev,
      _otherLinksArray: JSON.stringify(filtered),
    }));
  }, [otherLinksInputs, setFormData]);

  React.useEffect(() => {
    const filtered = investorsInputs.filter(s => s.trim() !== '');
    setFormData(prev => ({
      ...prev,
      _investorsNameArray: JSON.stringify(filtered),
    }));
  }, [investorsInputs, setFormData]);

  // Update formData with SDG boolean
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      _sustainableDevelopmentGoalBoolean: JSON.stringify(isSDGFocused ?? undefined),
    }));
  }, [isSDGFocused, setFormData]);

  // Convert temporary fields to proper format before exposing to parent
  React.useEffect(() => {
    // console.log('CompanyStep useEffect triggered', {
    //   formData,
    //   onCreateProfile: !!onCreateProfile,
    // });
    if (onCreateProfile) {
      // Parse JSON strings back to arrays/booleans
      let socialMediaUrlsParsed: string[] = [];
      let otherLinksParsed: string[] = [];
      let investorsNameParsed: string[] = [];
      let selectedSDGsParsed: number[] = [];
      let hasExistingInvestorsParsed = false;

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const socialStr = (formData as any)._socialMediaUrlsArray;
        if (socialStr) {
          socialMediaUrlsParsed = JSON.parse(socialStr);
        }
      } catch {
        // If parsing fails, try backward compatibility
        if (formData.socialMediaUrls) {
          const socialValue = formData.socialMediaUrls;
          if (typeof socialValue === 'string') {
            socialMediaUrlsParsed = socialValue.split(', ').filter((s: string) => s);
          } else if (
            Array.isArray(socialValue) &&
            socialValue.length > 0 &&
            typeof socialValue[0] === 'string'
          ) {
            socialMediaUrlsParsed = socialValue as string[];
          }
        }
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const otherStr = (formData as any)._otherLinksArray;
        if (otherStr) {
          otherLinksParsed = JSON.parse(otherStr);
        }
      } catch {
        if (formData.otherLinks) {
          const otherValue = formData.otherLinks;
          if (typeof otherValue === 'string') {
            otherLinksParsed = otherValue.split(', ').filter((s: string) => s);
          } else if (
            Array.isArray(otherValue) &&
            otherValue.length > 0 &&
            typeof otherValue[0] === 'string'
          ) {
            otherLinksParsed = otherValue as string[];
          }
        }
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const investorsStr = (formData as any)._investorsNameArray;
        if (investorsStr) {
          investorsNameParsed = JSON.parse(investorsStr);
        }
      } catch {
        if (formData.investorsName) {
          const investorsValue = formData.investorsName;
          if (typeof investorsValue === 'string') {
            investorsNameParsed = investorsValue.split(', ').filter((s: string) => s);
          } else if (
            Array.isArray(investorsValue) &&
            investorsValue.length > 0 &&
            typeof investorsValue[0] === 'string'
          ) {
            investorsNameParsed = investorsValue as string[];
          }
        }
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sdgsStr = (formData as any)._selectedSDGsArray;
        if (sdgsStr) {
          selectedSDGsParsed = JSON.parse(sdgsStr);
        }
      } catch {
        if (formData.selectedSDGs) {
          const sdgsValue = formData.selectedSDGs;
          if (typeof sdgsValue === 'string') {
            selectedSDGsParsed = sdgsValue
              .split(',')
              .map((n: string) => parseInt(n.trim()))
              .filter((n: number) => !isNaN(n));
          } else if (Array.isArray(sdgsValue)) {
            selectedSDGsParsed = sdgsValue
              .map((n: string | number) => (typeof n === 'string' ? parseInt(n.trim()) : n))
              .filter((n: number) => !isNaN(n));
          }
        }
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const investorsBoolStr = (formData as any)._hasExistingInvestorsBoolean;
        if (investorsBoolStr) {
          hasExistingInvestorsParsed = JSON.parse(investorsBoolStr);
        }
      } catch {
        // If parsing fails, use false
      }

      // Build converted data object without temporary fields
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        _socialMediaUrlsArray: _unused1,
        _otherLinksArray: _unused2,
        _investorsNameArray: _unused3,
        _selectedSDGsArray: _unused4,
        _sustainableDevelopmentGoalBoolean: _unused5,
        _hasExistingInvestorsBoolean: _unused6,
        socialMediaUrls: _unused7,
        otherLinks: _unused8,
        investorsName: _unused9,
        selectedSDGs: _unused10,
        sustainableDevelopmentGoal: _unused11,
        hasExistingInvestors: _unused12,
        ...restFormData
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } = formData as any;

      const convertedData = {
        ...restFormData,
        url: normalizeExternalUrl(restFormData.url) || restFormData.url,
        // Convert arrays and booleans from temporary fields
        socialMediaUrls: socialMediaUrlsParsed,
        otherLinks: otherLinksParsed,
        investorsName: investorsNameParsed,
        selectedSDGs: isSDGFocused === true ? selectedSDGs : [],
        sustainableDevelopmentGoal: isSDGFocused === null ? undefined : isSDGFocused,
        hasExistingInvestors: hasExistingInvestorsParsed,
      };

      // console.log('CompanyStep calling onCreateProfile with:', convertedData);
      onCreateProfile(convertedData);
    }
  }, [formData, onCreateProfile, isSDGFocused, selectedSDGs]);

  // Track if we need to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);
  const [investorNameErrors, setInvestorNameErrors] = React.useState<Record<number, string>>({});

  // Listen for validation requests from parent (layout effect so save is blocked synchronously)
  React.useLayoutEffect(() => {
    if (!shouldValidate) return;

    const requiredFields = [
      'companyName',
      'url',
      'country',
      'city',
      'slogan',
      'pitchSummary',
      'pitchDeckUrl',
      ...(countryRequiresStateSubdivision(String(formData.country || ''))
        ? (['state'] as const)
        : []),
    ];
    let fieldsValid = validateForm([...requiredFields]);
    const pitchSummary = String(formData.pitchSummary || '').trim();
    if (pitchSummary.length > 0 && pitchSummary.length < 80) {
      fieldsValid = false;
      setErrors(prev => ({ ...prev, pitchSummary: 'Minimum 80 characters' }));
    }

    const nameErrors: Record<number, string> = {};
    if (hasInvestors) {
      const filled = investorsInputs.map(name => name.trim()).filter(Boolean);
      if (filled.length === 0) {
        nameErrors[0] = 'Enter first and last name';
        fieldsValid = false;
      }
      investorsInputs.forEach((name, index) => {
        const words = name.trim().split(/\s+/).filter(Boolean);
        if (words.length === 1) {
          nameErrors[index] = 'Enter first and last name';
          fieldsValid = false;
        }
      });
    }
    setInvestorNameErrors(nameErrors);

    const sdgValid = isSDGFocused !== null;
    const isValid = fieldsValid && sdgValid;
    setShowErrors(true);

    onValidationResult?.(isValid);
  }, [
    shouldValidate,
    validateForm,
    onValidationResult,
    isSDGFocused,
    formData.pitchSummary,
    hasInvestors,
    investorsInputs,
    setErrors,
  ]);

  const pitchSummaryText = String(formData.pitchSummary || '').trim();
  const pitchSummaryNotice =
    pitchSummaryText.length < 80 && (pitchSummaryText.length > 0 || showErrors)
      ? 'Minimum 80 characters'
      : undefined;

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      {/* Company Section */}
      <FormSection icon={companyIcon} title="Company">
        <FormField
          name="companyName"
          label="Company Name *"
          required
          value={String(formData.companyName || '')}
          onChange={handleInputChange}
          error={showErrors ? errors.companyName : undefined}
          placeholder="Enter your company name"
        />

        <FormField
          name="url"
          label="Website URL *"
          type="url"
          required
          value={String(formData.url || '')}
          onChange={handleInputChange}
          error={showErrors ? errors.url : undefined}
          placeholder="https://yourcompany.com"
        />

        <div>
          <label
            htmlFor="country"
            className="block text-sm sm:text-base font-medium text-white mb-1.5 sm:mb-2 text-left"
          >
            Company Location *
          </label>
          <CountrySelect
            id="country"
            name="country"
            value={String(formData.country || '')}
            onChange={nextCountry => {
              const prevCountry = String(formData.country || '');
              handleInputChange({
                target: { name: 'country', value: nextCountry },
              } as React.ChangeEvent<HTMLInputElement>);
              if (
                countryRequiresStateSubdivision(prevCountry) !==
                  countryRequiresStateSubdivision(nextCountry) ||
                isUnitedStatesCountry(prevCountry) !== isUnitedStatesCountry(nextCountry) ||
                isUnitedKingdomCountry(prevCountry) !== isUnitedKingdomCountry(nextCountry)
              ) {
                handleInputChange({
                  target: { name: 'state', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            required
            className={`w-full p-2 sm:px-3 sm:py-2 text-sm sm:text-base border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 appearance-none ${
              showErrors && errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '16px 16px',
              paddingRight: '40px',
            }}
          />
          {showErrors && errors.country ? (
            <p className="text-red-500 text-xs sm:text-sm mt-1 text-right">{errors.country}</p>
          ) : null}
        </div>

        {isUnitedStatesCountry(String(formData.country || '')) && (
          <div>
            <label
              htmlFor="state"
              className={`block text-sm sm:text-base font-medium mb-1.5 sm:mb-2 text-left ${
                showErrors && errors.state ? 'text-red-500' : 'text-white'
              }`}
            >
              State *
            </label>
            <select
              id="state"
              name="state"
              value={String(formData.state || '')}
              onChange={e => {
                handleInputChange(e);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              required
              className={`w-full p-2 sm:px-3 sm:py-2 text-sm sm:text-base border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 appearance-none ${
                showErrors && errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
              style={NATIVE_SELECT_CHEVRON_STYLE}
            >
              <option value="">Select state</option>
              {(String(formData.state || '').trim() &&
              !usStates.includes(String(formData.state || '').trim())
                ? [String(formData.state || '').trim(), ...usStates]
                : usStates
              ).map(state => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {showErrors && errors.state ? (
              <p className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                Please select your state.
              </p>
            ) : null}
          </div>
        )}

        {isUnitedKingdomCountry(String(formData.country || '')) && (
          <div>
            <label
              htmlFor="state"
              className={`block text-sm sm:text-base font-medium mb-1.5 sm:mb-2 text-left ${
                showErrors && errors.state ? 'text-red-500' : 'text-white'
              }`}
            >
              County *
            </label>
            <select
              id="state"
              name="state"
              value={String(formData.state || '')}
              onChange={e => {
                handleInputChange(e);
                handleInputChange({
                  target: { name: 'city', value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              required
              className={`w-full p-2 sm:px-3 sm:py-2 text-sm sm:text-base border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 appearance-none ${
                showErrors && errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
              style={NATIVE_SELECT_CHEVRON_STYLE}
            >
              <option value="">Select county</option>
              {(String(formData.state || '').trim() &&
              !(UK_COUNTIES as readonly string[]).includes(String(formData.state || '').trim())
                ? [String(formData.state || '').trim(), ...UK_COUNTIES]
                : UK_COUNTIES
              ).map(county => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
            {showErrors && errors.state ? (
              <p className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                Please select your county.
              </p>
            ) : null}
          </div>
        )}

        {String(formData.country || '').trim() ? (
          <div>
            <label
              htmlFor="city"
              className="block text-sm sm:text-base font-medium text-white mb-1.5 sm:mb-2 text-left"
            >
              City *
            </label>
            <CitySelect
              id="city"
              name="city"
              country={String(formData.country || '')}
              region={String(formData.state || '')}
              value={String(formData.city || '')}
              required
              onChange={city =>
                handleInputChange({
                  target: { name: 'city', value: city },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              className={`w-full p-2 sm:px-3 sm:py-2 text-sm sm:text-base border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 ${
                showErrors && errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              error={Boolean(showErrors && errors.city)}
            />
            {showErrors && errors.city ? (
              <p className="text-red-500 text-xs sm:text-sm mt-1 text-right">{errors.city}</p>
            ) : null}
          </div>
        ) : null}
      </FormSection>

      {/* Pitch Section */}
      <FormSection icon={pitchIcon} title="Pitch">
        <FormField
          name="slogan"
          label="Slogan *"
          required
          value={String(formData.slogan || '')}
          onChange={handleInputChange}
          error={showErrors ? errors.slogan : undefined}
          placeholder="Enter your company slogan"
        />

        <TextArea
          name="pitchSummary"
          label="Pitch Summary *"
          required
          value={String(formData.pitchSummary || '')}
          onChange={handleInputChange}
          minCharacters={80}
          maxCharacters={500}
          rows={5}
          error={pitchSummaryNotice ?? (showErrors ? errors.pitchSummary : undefined)}
          placeholder="Describe your company in a few sentences (minimum 80 characters)..."
        />

        <FileUpload
          name="pitchDeck"
          label="Update your Pitch Deck"
          description="PDF in standard widescreen format (in English)"
          icon={writeIcon}
          required
          accept="application/pdf"
          maxSizeMB={5}
          onFileSelect={handleFileSelect}
          status={uploadingFile ? uploadProgress : undefined}
          fileName={pitchDeckFileName || undefined}
          validationError={showErrors && !formData.pitchDeckUrl ? errors.pitchDeckUrl : undefined}
        />
        {uploadError && <p className="text-red-500 text-sm mt-1">{uploadError}</p>}
        {uploadingFile && (
          <div className="flex items-center gap-2 text-emerald-500 text-sm">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {uploadProgress}
          </div>
        )}

        <FormField
          name="videoPitchUrl"
          label="Link to a video pitch"
          type="url"
          value={String(formData.videoPitchUrl || '')}
          onChange={handleInputChange}
          placeholder="https://youtube.com/watch?v=..."
        />

        <div>
          <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
            Company&apos;s social media profiles
          </label>
          {socialMediaInputs.map((value, index) => (
            <div key={index} className="mb-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                <div className="flex-1">
                  <FormField
                    name={`socialMedia-${index}`}
                    label=""
                    value={value}
                    onChange={(e: any) => {
                      const newInputs = [...socialMediaInputs];
                      newInputs[index] = e.target.value;
                      setSocialMediaInputs(newInputs);
                    }}
                    placeholder="LinkedIn, X, Instagram, etc."
                  />
                </div>
                <div className="flex gap-2 mt-2 sm:mt-0">
                  {index === 0 && (
                    <AddButton onClick={() => setSocialMediaInputs([...socialMediaInputs, ''])} />
                  )}
                  {index > 0 && (
                    <RemoveButton
                      onClick={() => {
                        const newInputs = socialMediaInputs.filter((_, i) => i !== index);
                        setSocialMediaInputs(newInputs);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div>
          <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
            Other information links, such as press, media, etc.
          </label>
          {otherLinksInputs.map((value, index) => (
            <div key={index} className="mb-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                <div className="flex-1">
                  <FormField
                    name={`otherLinks-${index}`}
                    label=""
                    type="url"
                    value={value}
                    onChange={(e: any) => {
                      const newInputs = [...otherLinksInputs];
                      newInputs[index] = e.target.value;
                      setOtherLinksInputs(newInputs);
                    }}
                    placeholder="https://example.com/news"
                  />
                </div>
                <div className="flex gap-2 mt-2 sm:mt-0">
                  {index === 0 && (
                    <AddButton onClick={() => setOtherLinksInputs([...otherLinksInputs, ''])} />
                  )}
                  {index > 0 && (
                    <RemoveButton
                      onClick={() => {
                        const newInputs = otherLinksInputs.filter((_, i) => i !== index);
                        setOtherLinksInputs(newInputs);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <RadioButton
          name="sustainableDevelopmentGoal"
          value={isSDGFocused}
          onChange={value => {
            setIsSDGFocused(value);
            if (!value) {
              setSelectedSDGs([]);
            }
          }}
          label="Is your startup focused on impacting any specific Sustainable Development Goal? *"
          error={showErrors && isSDGFocused === null ? 'This field is required' : undefined}
        />

        {isSDGFocused === true && (
          <div className="mt-6">
            <SDGSelector selectedSDGs={selectedSDGs} onSDGSelect={handleSDGSelect} />
          </div>
        )}
      </FormSection>

      <FormSection icon={investorsIcon} title="Investors">
        <Toggle
          name="hasExistingInvestors"
          value={hasInvestors}
          onChange={setHasInvestors}
          label="Are there existing investors?"
        />

        {hasInvestors && (
          <div className="mt-4">
            <label className="text-sm sm:text-base font-medium text-white mb-2 block text-left">
              Investors
            </label>
            {investorsInputs.map((value, index) => (
              <div key={index} className="mb-2">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
                  <div className="flex-1">
                    <FormField
                      name={`investors-${index}`}
                      label=""
                      value={value}
                      onChange={(e: any) => {
                        const newInputs = [...investorsInputs];
                        newInputs[index] = e.target.value;
                        setInvestorsInputs(newInputs);
                        setInvestorNameErrors(prev => {
                          if (!prev[index]) return prev;
                          const next = { ...prev };
                          delete next[index];
                          return next;
                        });
                      }}
                      placeholder="First and last name"
                      error={showErrors ? investorNameErrors[index] : undefined}
                    />
                  </div>
                  <div className="flex gap-2 mt-2 sm:mt-0">
                    {index === 0 && (
                      <AddButton onClick={() => setInvestorsInputs([...investorsInputs, ''])} />
                    )}
                    {index > 0 && (
                      <RemoveButton
                        onClick={() => {
                          const newInputs = investorsInputs.filter((_, i) => i !== index);
                          setInvestorsInputs(newInputs);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </FormSection>
    </BaseStep>
  );
};

export default CompanyStep;
